import { Injectable } from "@angular/core";
import firebase from 'firebase';
import { CompanyDetails, UrlHandles, Product, CountryCodes, CompanyListModel, ProductMaster, CompanyCategoryEnum } from '../models/company.model';
import { Cloudinary } from '@cloudinary/angular-5.x';
import { HttpClient, HttpHeaders } from "@angular/common/http";

import { environment } from '../environment/environment';
import { InvestorTypesEnum } from "../models/investor.model";
import { RequiredEmployeeModel } from "../models/employee.model";

@Injectable()
export class CompanyService {
    companyDetailsRef = firebase.database().ref('/companyDetails');
    backendURL = environment.backendURL;

    constructor(public cloudinary: Cloudinary, public http: HttpClient) { }

    addHireEmployeeRequest(companyDetails: CompanyDetails, hireEmp: RequiredEmployeeModel) {

        if (hireEmp.cid) {
            hireEmp.modifiedOn = new Date().toDateString();
        } else {
            hireEmp.createdOn = new Date().toDateString();
            hireEmp.modifiedOn = hireEmp.createdOn;

            hireEmp.cid = companyDetails.id;
            hireEmp.cName = companyDetails.title;
            hireEmp.cImage = companyDetails.images[0];
            hireEmp.currency = companyDetails.currency;
            hireEmp.city = companyDetails.city;
            hireEmp.country = companyDetails.country;
        }

        return new Promise((resolve, reject) => {
            firebase.database().ref('hireEmployeeRequests').child(companyDetails.id).set(hireEmp).then(() => {
                firebase.database().ref('investments/companies').child(companyDetails.id).update({
                    employeePeriod: hireEmp.period
                });
                return resolve();
            }, error => {
                return reject();
            });
        });
    }

    checkHireEmployeeRequests(cid: string): Promise<RequiredEmployeeModel> {
        return new Promise<RequiredEmployeeModel>((resolve, reject) => {
            firebase.database().ref('hireEmployeeRequests').child(cid).once('value', snapshot => {
                if (snapshot.val())
                    return resolve(snapshot.val());

                return reject("No data found");
            });
        });
    }

    getListingEmploymentSearch(city: string): Promise<Array<RequiredEmployeeModel>> {

        return new Promise<Array<RequiredEmployeeModel>>((resolve, reject) => {
            firebase.database().ref('hireEmployeeRequests')
                .orderByChild('city')
                .equalTo(city)
                .once('value', snapshot => {

                    if (snapshot && snapshot.val()) {
                        return resolve(Object.values(snapshot.val()));
                    }

                    return reject("No data found");
                }, error => {
                    return reject();
                });
        });
    }

    getCompanyCategories(): Promise<Array<string>> {

        return new Promise<Array<string>>((resolve, reject) => {
            firebase.database().ref('companyCategories/categories').once('value', snapshot => {
                if (snapshot.val()) {
                    return resolve(Object.values(snapshot.val()));
                } else {
                    return reject("No categories found");
                }
            });
        });
    }

    getCompanySubCategories(category: string): Promise<Array<string>> {
        var databaseRef = "";
        if (category == CompanyCategoryEnum.Food) {
            databaseRef = 'companyCategories/subCategories/' + category + '/filterTypes';
        } else if (category == CompanyCategoryEnum.Markets) {
            databaseRef = 'companyCategories/subCategories/' + category;
        }

        return new Promise<Array<string>>((resolve, reject) => {
            firebase.database().ref(databaseRef)
                .once('value', snapshot => {
                    if (snapshot.val()) {
                        return resolve(Object.values(snapshot.val()));
                    } else {
                        return reject("No subcategories found");
                    }
                });
        });
    }

    getCompanyTypes(subCategory: string): Promise<Array<string>> {

        return new Promise<Array<string>>((resolve, reject) => {
            firebase.database().ref('companyCategories/subCategories/Food/' + subCategory).once('value', snapshot => {
                if (snapshot.val()) {
                    return resolve(Object.values(snapshot.val()));
                } else {
                    return reject("No company types found");
                }
            });
        });
    }

    getFilters(type: string): Promise<any> {

        return new Promise<any>((resolve, reject) => {
            firebase.database().ref('filters')
                .child(type)
                .once('value', snapshot => {
                    if (snapshot.val()) {
                        return resolve(snapshot.val());
                    } else {
                        return reject("No filters found");
                    }
                }, error => {
                    this.handleError(error);
                });
        });
    }

    checkIfDuplicateHandle(value): Promise<boolean> {

        return new Promise<boolean>((resolve, reject) => {

            firebase.database().ref('/urlHandles')
                .orderByChild('handle')
                .equalTo(value)
                .once('value', snapshot => {

                    if (snapshot.val())
                        return resolve(true);

                    return resolve(false);
                }, error => {
                    this.handleError(error);
                });
        });
    }

    getUrlHandlesList(): Promise<Array<UrlHandles>> {

        return new Promise<Array<UrlHandles>>((resolve, reject) => {

            firebase.database().ref('/urlHandles')
                .once('value', snapshot => {
                    if (snapshot.val()) {
                        return resolve(Object.values(snapshot.val()));
                    }
                    return reject("No URL handles found");
                }, error => {
                    this.handleError(error);
                });
        });
    }

    getCompanyDetailsByUserId(uid: string): Promise<CompanyDetails> {

        return new Promise<CompanyDetails>((resolve, reject) => {

            this.companyDetailsRef
                .orderByChild('uid')
                .equalTo(uid)
                .once('value', snapshot => {
                    if (snapshot.val()) {
                        return resolve(Object.values(snapshot.val())[0] as CompanyDetails);
                    }
                    else {
                        return reject("Company not found");
                    }
                }, error => {
                    this.handleError(error);
                });
        });
    }

    getCompanyDetails(cid: string): Promise<CompanyDetails> {

        return new Promise<CompanyDetails>((resolve, reject) => {

            firebase.database().ref('/companyDetails/' + cid).once('value', snapshot => {
                return resolve(snapshot.val());
            }, error => {
                this.handleError(error);
            });
        });
    }

    async createCompanyPage(urlHandleObj: UrlHandles, companyDetails: CompanyDetails, investmentType?: InvestorTypesEnum, downPayment?: number): Promise<CompanyDetails> {
        companyDetails.createdOn = Date.now();
        let companyId = this.generatePushID();
        companyDetails.id = companyId;
        urlHandleObj.companyId = companyId;

        this.createEntryInUrlHandlesArray(urlHandleObj);

        let data = {
            companyId: companyId,
            companyDetails: companyDetails
        };
        let token;

        if (companyDetails.isInvestmentRequired == 'yes' && investmentType) {
            data['investmentType'] = investmentType;
            data['downPayment'] = +(downPayment);
        } else {
            companyDetails.isInvestmentRequired = 'no';
        }

        await firebase.auth().currentUser.getIdToken(true).then(idToken => {
            token = idToken;
        }).catch(error => {
            return Promise.reject(error);
        });

        let httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': token
            })
        };

        return new Promise<CompanyDetails>((resolve, reject) => {

            this.http.post((this.backendURL + 'createCompany'), data, httpOptions)
                .subscribe((res) => {

                    if (res['result'] == 'success') {
                        this.updateUserProfileWithCompanyStatusAndId(companyDetails.uid, true, companyId);
                        return resolve();
                    }
                }, error => {
                    this.handleError(error);
                });
        });
    }

    getCompanyIdFromUrlHandle(urlHandle: string): Promise<UrlHandles> {

        return new Promise<UrlHandles>((resolve, reject) => {

            firebase.database().ref('/urlHandles')
                .orderByChild('handle')
                .equalTo(urlHandle)
                .once('value', snapshot => {

                    if (snapshot) {
                        if (snapshot.val())
                            return resolve(Object.values(snapshot.val())[0] as UrlHandles);
                    }

                    return reject();
                }).catch(error => this.handleError(error));
        });
    }

    getUrlHandleFromCompanyId(cid: string): Promise<string> {

        return new Promise<string>((resolve, reject) => {

            firebase.database().ref('/urlHandles')
                .child(cid)
                .child('handle')
                .once('value', snapshot => {

                    if (snapshot) {
                        if (snapshot.val())
                            return resolve(snapshot.val());
                    }

                    return reject();
                }).catch(error => this.handleError(error));
        });
    }

    // Sets a flag in user profiel to say that the user has a published company page
    updateUserProfileWithCompanyStatusAndId(uid: string, status: boolean, companyId: string): void {

        firebase.database().ref('/users/' + uid).child('user').update({
            hasCompany: status,
            updatedOn: new Date().toString(),
            companyId: companyId
        });
    }

    updateCompanyPage(companyDetails: CompanyDetails): Promise<CompanyDetails> {
        companyDetails.updatedOn = new Date().toString();

        return new Promise<CompanyDetails>((resolve, reject) => {

            firebase.database().ref('/companyDetails/' + companyDetails.id).update(companyDetails).then((response) => {
                return resolve();
            }, error => {
                this.handleError(error);
            });
        });
    }

    async createEntryInUrlHandlesArray(urlHandle: UrlHandles): Promise<any> {
        urlHandle.createdOn = new Date().toString();

        let data = {
            companyId: urlHandle.companyId,
            urlHandle: urlHandle
        };
        let token;

        await firebase.auth().currentUser.getIdToken(true).then(idToken => {
            token = idToken;
        }).catch(error => {
            return Promise.reject(error);
        });

        let httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': token
            })
        };

        return new Promise((resolve, reject) => {

            this.http.post((this.backendURL + 'createUrlHandle'), data, httpOptions)
                .subscribe((res) => {
                    if (res['result'] == 'success')
                        return resolve();
                }, error => {
                    this.handleError(error)
                });
        });
    }

    addNewProduct(cid: string, product: Product, worth: number) {
        product.createdOn = new Date().toString();
        let productId = this.generatePushID();
        product.id = productId;
        let idCount: number;

        if (!product.image) {
            product.image = environment.defaultImages.notAvailable;
        }

        return new Promise<Product>((resolve, reject) => {

            firebase.database().ref('/productMaster/idCount').once('value', snapshot => {

                idCount = snapshot.val();
                idCount++;
                firebase.database().ref('/productMaster/idCount').set(idCount);
                product.plu = idCount;

                firebase.database().ref('/companyDetails/' + cid + '/products/' + productId).set(product).then((response) => {

                    this.addProductToProductMaster(product, cid);

                    this.updateCompanyWorth(cid, product, worth);
                    return resolve();
                }, error => {
                    this.handleError(error);
                })
            });
        });
    }

    updateProduct(cid: string, product: Product) {
        product.updatedOn = new Date().toString();

        return new Promise<Product>((resolve, reject) => {

            firebase.database().ref('/companyDetails/' + cid + '/products/' + product.id).update(product).then((response) => {
                return resolve();
            }, error => {
                this.handleError(error);
            })
        });
    }

    addProductToProductMaster(product: Product, cid: string) {

        let productMaster = new ProductMaster(product, cid);

        firebase.database().ref('/productMaster/' + productMaster.productID).set(productMaster);

    }

    getProductByPLU(code: string): Promise<ProductMaster> {
        try {
            // let salesRepCode = code.split("|")[1];
            let plu = parseInt(code.split("|")[0]);

            return new Promise<ProductMaster>((resolve, reject) => {

                // if (plu != NaN && salesRepCode) {
                if (!isNaN(plu)) {

                    firebase.database().ref('/productMaster')
                        .orderByChild('plu')
                        .equalTo(plu)
                        .once('value', snapshot => {

                            console.log(plu);
                            console.log(snapshot.val());

                            let temp = Object.values(snapshot.val())[0];
                            let productMaster = temp as ProductMaster;

                            firebase.database().ref('/companyDetails/' + productMaster.companyID + '/products/' + productMaster.productID).once('value', snapshot => {
                                productMaster.product = snapshot.val();
                                return resolve(productMaster);
                            }).catch(error => {
                                return reject(error.message || "Error fetching data");
                            });
                        }).catch(error => {
                            return reject(error.message || "Error fetching data");
                        });
                } else {
                    return reject();
                }
            });
        }
        catch (error) {
            return Promise.reject(error.message || error);
        }
    }

    getProductById(cid: string, pid: string) {

        try {
            return new Promise((resolve, reject) => {

                firebase.database().ref('/companyDetails')
                    .child(cid)
                    .child('products')
                    .child(pid).once('value', snapshot => {

                        if (snapshot && snapshot.val()) {
                            return resolve(snapshot.val());
                        }

                        return reject();
                    });
            });
        } catch(error) {
            return Promise.reject(error.message || error);
        }
    }

    updateCompanyWorth(cid: string, product: Product, worth: number) {

        firebase.database().ref('/companyDetails/' + cid).update({
            updatedOn: new Date().toString(),
            worth: (worth + (product.quantity * product.pricePerUnit))
        })
    }

    getProductWithTitle(cid: string, title: string) {

        return new Promise<Product>((resolve, reject) => {

            firebase.database().ref('/companyDetails/' + cid)
                .child('products')
                .orderByChild('title')
                .equalTo(title)
                .once('value', snapshot => {
                    return resolve(snapshot.val());
                }, error => {
                    this.handleError(error);
                })
        });
    }

    getCodesForCountry(country): Promise<CountryCodes> {

        return new Promise<CountryCodes>((resolve, reject) => {
            firebase.database().ref('/countryCodes')
                .child(country)
                .once('value', snapshot => {
                    return resolve(snapshot.val() as CountryCodes);
                })
                .catch(error => {
                    this.handleError(error);
                });
        });
    }

    addImagesToCompanyPage(cid, url, length) {
        length = parseInt(length) - 1;

        return new Promise((resolve, reject) => {
            firebase.database().ref('/companyDetails/' + cid).child('images').child(length).set(url);
        });
    }

    removeImage(cid, imgPublicId) {
        let companyRef = firebase.database().ref('/companyDetails/' + cid + '/images');

        return companyRef.once('value', snapshot => {
            if (snapshot.val()) {
                snapshot.val().forEach(element => {
                    if (element == imgPublicId) {
                        // companyRef.orderByValue().equalTo(imgPublicId).ge
                    }
                });
            }
        });
    }

    getCompanyForListFromId(id): Promise<CompanyListModel> {

        return new Promise<CompanyListModel>((resolve, reject) => {

            this.companyDetailsRef.child(id)
                .once('value', snapshot => {

                    if (snapshot.val()) {
                        return resolve(this.mapCompanyDetailsToList(snapshot.val()));
                    } else {
                        return reject("Company not found");
                    }
                });
        });
    }

    async getAllCompaniesForList(list): Promise<Array<CompanyListModel>> {
        let companyList = new Array<CompanyListModel>();

        return new Promise<Array<CompanyListModel>>(async (resolve, reject) => {

            await list.forEach(async element => {

                await this.companyDetailsRef.child(element)
                    .once('value', snapshot => {

                        if (snapshot.val())
                            companyList.push(this.mapCompanyDetailsToList(snapshot.val()));
                    });
            });

            return resolve(companyList);
        });
    }

    getAllCompaniesForCity(place): Promise<Array<CompanyDetails>> {

        return new Promise<Array<CompanyDetails>>((resolve, reject) => {

            this.companyDetailsRef
                .orderByChild('city')
                .equalTo(place)
                .once('value', snapshot => {

                    if (snapshot.val()) {
                        var tempArr = Object.values(snapshot.val()) as Array<CompanyDetails>;

                        return resolve(tempArr);
                    } else {
                        return reject();
                    }
                });
        });
    }

    getCompanyListing(type: string, place: string): Promise<Array<CompanyListModel>> {
        var key = type.concat(place);
        return new Promise<Array<any>>((resolve, reject) => {

            this.companyDetailsRef
                .orderByChild('categoryCityKey')
                .equalTo(key)
                .once('value', snapshot => {

                    if (snapshot.val()) {
                        var tempArr = Object.values(snapshot.val());
                        tempArr = tempArr.map(element => this.mapCompanyDetailsToList(element)) as Array<CompanyListModel>;

                        return resolve(tempArr);
                    } else {
                        return reject("No companies found");
                    }
                });
        });
    }

    mapCompanyDetailsToList(element): CompanyListModel {
        let company = new CompanyListModel();
        company.description = element.description;
        company.id = element.id;
        if (element.images) {
            company.image = element.images[0];
        } else {
            company.image = environment.defaultImages.notAvailable;
        }
        company.name = element.title;
        company.followerCount = element.followerCount;
        company.category = element.category;
        company.subCategory = element.subCategory;
        company.type = element.type;
        company.worth = element.worth;
        company.categorizedAs = element.categorizedAs;
        company.city = element.city;
        company.country = element.country;

        return company;
    }

    followThis(cid, follow) {
        follow.createdOn = Date.now();

        return new Promise((resolve, reject) => {

            firebase.database().ref('/companyDetails/' + cid)
                .child('following')
                .push(follow)
                .then(snap => {
                    if (snap && snap.key) {
                        return resolve();
                    } else {
                        return reject();
                    }
                })
        });
    }

    stopFollowing(uid, cid) {

        return new Promise((resolve, reject) => {

            firebase.database().ref('/companyDetails/' + cid)
                .child('following')
                .orderByChild('id')
                .equalTo(uid)
                .once('value', snapshot => {

                    if (snapshot.val()) {
                        var idToRemove = Object.keys(snapshot.val())[0];

                        firebase.database().ref('/companyDetails/' + cid)
                            .child('following')
                            .child(idToRemove)
                            .remove();
                    }
                });
        });
    }

    getTop5Companies(city) {
        return new Promise((resolve, reject) => {

            firebase.database().ref('/companyDetails')
                .orderByChild('city')
                .equalTo(city)
                .once('value', snapshot => {
                    if (snapshot.val()) {
                        var tempArr = Object.values(snapshot.val());
                        tempArr = tempArr.map(element => this.mapCompanyDetailsToList(element));
                        return resolve((this.sortByFollowerCount(tempArr)));
                    }
                    return resolve();
                }, error => reject(error));
        });
    }

    // TODO: reduce complexity (merge sort)
    sortByFollowerCount(values) {

        var len = values.length;
        for (var i = len - 1; i >= 0; i--) {
            for (var j = 1; j <= i; j++) {
                if (values[j - 1].followerCount < values[j].followerCount) {
                    var temp = values[j - 1];
                    values[j - 1] = values[j];
                    values[j] = temp;
                }
            }
        }

        return values;
    }

    reverseArray(arr) {
        let revArr = new Array<Object>();

        for (let i = arr.length - 1; i >= 0; i--) {
            revArr.push(arr[i]);
        }

        return revArr;
    }

    private handleError(error: any): Promise<any> {
        console.error('An error occurred', error);
        return Promise.reject(error.message || error);
    }

    private generatePushID = (function () {
        // Modeled after base64 web-safe chars, but ordered by ASCII.
        var PUSH_CHARS = '-0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz';

        // Timestamp of last push, used to prevent local collisions if you push twice in one ms.
        var lastPushTime = 0;

        // We generate 72-bits of randomness which get turned into 12 characters and appended to the
        // timestamp to prevent collisions with other clients.  We store the last characters we
        // generated because in the event of a collision, we'll use those same characters except
        // "incremented" by one.
        var lastRandChars = [];

        return function () {
            var now = new Date().getTime();
            var duplicateTime = (now === lastPushTime);
            lastPushTime = now;

            var timeStampChars = new Array(8);
            for (var i = 7; i >= 0; i--) {
                timeStampChars[i] = PUSH_CHARS.charAt(now % 64);
                // NOTE: Can't use << here because javascript will convert to int and lose the upper bits.
                now = Math.floor(now / 64);
            }
            if (now !== 0) throw new Error('We should have converted the entire timestamp.');

            var id = timeStampChars.join('');

            if (!duplicateTime) {
                for (i = 0; i < 12; i++) {
                    lastRandChars[i] = Math.floor(Math.random() * 64);
                }
            } else {
                // If the timestamp hasn't changed since last push, use the same random number, except incremented by 1.
                for (i = 11; i >= 0 && lastRandChars[i] === 63; i--) {
                    lastRandChars[i] = 0;
                }
                lastRandChars[i]++;
            }
            for (i = 0; i < 12; i++) {
                id += PUSH_CHARS.charAt(lastRandChars[i]);
            }
            if (id.length != 20) throw new Error('Length should be 20.');

            return id;
        };
    })();
}


// WEBPACK FOOTER //
// ./src/services/company.service.ts